import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { environment } from '@hub-workspace/apps/stock/src/environments/environment';
import { Store } from '@ngrx/store';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { OperationType } from '@toro/common/shared/enum/orders/operation-type.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { ConsolidatedPosition } from '@toro/common/shared/models/pos-risk/consolidated-position';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { FinanceActions } from '@toro/common/shared/state-management/finance/finance.action';
import { ResearchActions } from '@toro/common/shared/state-management/research/research.action';
import { filter, map } from 'rxjs/operators';
import { ICheckoutStock } from '../../../../../interfaces/checkout.interface';
import { calculateInvestment, getStopVariation, roundPointsInvestment, stressCalc } from '../../utils/investment-calculation';
import { CheckoutAboutFeesComponent } from '../checkout-about-fees/checkout-about-fees.component';
import { DialogPriceEditionComponent } from '../dialog-price-edittion/dialog-price-edition.component';
@Component({
  selector: 'checkout-automatic',
  templateUrl: './checkout-automatic.component.html',
  styleUrls: ['checkout-automatic.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CheckoutAutomaticComponent extends DisposableSafelly implements OnInit {
  public isNewBrand = environment.isNewBrand;

  @Input() public stock: ICheckoutStock;
  @Input() public getInvestments: number;
  @Output() public close = new EventEmitter();

  public settings: any;
  public dialogComponentReference: MatDialogRef<DialogPriceEditionComponent>;
  public gainPriceCalculated: number;
  public lossPriceCalculated: number;
  public closingTimeDayTrade: string | Date;
  public closingTimeBovespa: string | Date;
  public consolidatedPosition: ConsolidatedPosition;
  public dialogAboutFeesReference: MatDialogRef<CheckoutAboutFeesComponent>;
  public typeMarketEnum = OrderMarketTypeEnum;

  public tooltipBalloon: string = `<div>
        <div class='pop-content font-gray-color--darker'>
            <strong>Corretagem Zero</strong>
            <p>Não existe cobrança de corretagem quando você escolhe automatizar seus investimentos para que eles encerrem sozinhos ao atingir um lucro ou prejuízo específico.</p>
        </div>
    </div>`;

  constructor(
    protected dialogsServices: DialogsService,
    private store: Store<AppState>,
  ) {
    super();
  }

  public get stressCalculation() { return stressCalc(this.stock.purchase, this.stock.stress.value, this.stock.stress.dayTradeValue, this.stock.isDayTrade, this.stock.typeMarket); }
  public get gainPrice() { return Math.ceil(this.calculateStopPrice(this.stock.purchase, true, this.getUnitGainVariation()) * 100) / 100; }
  public set gainPrice(value: number) { this.gainPriceCalculated = value; }
  public get lossPrice() { return Math.ceil(this.calculateStopPrice(this.stock.purchase, false, this.getUnitLossVariation()) * 100) / 100; }
  public set lossPrice(value: number) { this.lossPriceCalculated = value; }
  public get isFuture() { return this.stock.typeMarket === OrderMarketTypeEnum.Future; }
  public get stopGainValue() { return this.gainPriceCalculated * this.stock.buyAmount * this.stock.quoteFactor; }
  public get stopLossValue() { return this.lossPriceCalculated * this.stock.buyAmount * this.stock.quoteFactor; }
  public get stopGainVariation() { return this.gainPriceCalculated * this.stock.buyAmount; }
  public get stopLossVariation() { return this.lossPriceCalculated * this.stock.buyAmount; }

  public ngOnInit() {
    this.stock.editedPrice = 0;
    this.tradingHoursSubscription();
    this.bindQuoteSubscription();
    this.updateGainLossValue();
    this.priceBandSubscription();

    this.store.select((state) => state.finance.consolidatePosition)
      .pipe(
        filter((consolidatePosition) => !!consolidatePosition))
      .subscribe(consolidatedPosition => this.consolidatedPosition = consolidatedPosition);

    this.store.dispatch({ type: FinanceActions.getRealtimeFinancePosition });
  }

  public tradingHoursSubscription() {
    this.disposableSubscriptions.push(
      this.store.select((store) => store.exchange?.tradingTime)
        .pipe(filter((data) => !!data))
        .subscribe((data) => {
          this.closingTimeBovespa = data.limitDayTradeStock;
          this.closingTimeDayTrade = data.limitDayTradeFuture;
        }));
  }

  public getUnitGainVariation() {
    if (this.stock.stopGainVariation) {
      return getStopVariation(this.stock, true, this.isFuture);
    }
    return this.stock.unitGainPrice;
  }

  public getUnitLossVariation() {
    if (this.stock.stopLossVariation) {
      return getStopVariation(this.stock, false, this.isFuture);
    }
    return this.stock.unitLossPrice;
  }

  public updateGainLossValue() {
    this.gainPrice = this.stock.purchase ? this.gainPrice - this.stock.unitPrice : this.stock.unitPrice - this.gainPrice;
    this.lossPrice = this.stock.purchase ? this.stock.unitPrice - this.lossPrice : this.lossPrice - this.stock.unitPrice;
  }

  public roundPointsInvestment(points: number) {
    return roundPointsInvestment(points);
  }

  // Abre a dialog para atualização de lucro
  public changeDealGain() {
    this.settings = {
      ...this.stock,
      isAutomatic: true,
      isGain: null,
      getInvestments: calculateInvestment(this.stock),
    };

    this.settings.isGain = true;
    this.settings.priceEdited = this.stopGainVariation;

    this.dialogComponentReference =
      (this.dialogsServices.showComponentAsAsidePanel(DialogPriceEditionComponent, true, this.settings) as
        MatDialogRef<DialogPriceEditionComponent>);
    this.disponsableEditPrice(this.dialogComponentReference, 'gain');
  }

  // Abre a dialog para atualização de prejuízo
  public changeDealLoss() {
    this.settings = {
      ...this.stock,
      isAutomatic: true,
      isGain: null,
      getInvestments: calculateInvestment(this.stock),
    };

    this.settings.isGain = false;
    this.settings.priceEdited = this.stopLossVariation;

    this.dialogComponentReference =
      (this.dialogsServices.showComponentAsAsidePanel(DialogPriceEditionComponent, true, this.settings) as
        MatDialogRef<DialogPriceEditionComponent>);
    this.disponsableEditPrice(this.dialogComponentReference, 'loss');
  }

  // Observa e atualiza os lotes que vieram editado da dialog
  public disponsableEditPrice(dialogReference, status) {
    this.disposableSubscriptions.push(
      dialogReference.componentInstance.saveEdit
        .subscribe((priceEdited) => {
          if (priceEdited) {
            switch (status) {
              case 'gain': this.updateGainVariationValues(priceEdited); break;
              case 'loss': this.updateLossVariationValues(priceEdited); break;
              default: return;
            }
          }
        }),
    );
  }

  public updateGainVariationValues(priceEdited) {
    this.gainPrice = this.stock.stopGainVariation = priceEdited / this.stock.buyAmount / this.stock.quoteFactor * (!this.isFuture ? this.stressCalculation : 1)
  }

  public updateLossVariationValues(priceEdited) {
    this.lossPrice = this.stock.stopLossVariation = priceEdited / this.stock.buyAmount / this.stock.quoteFactor * (!this.isFuture ? this.stressCalculation : 1)
  }

  public ngOnDestroy() {
    this.disposableSubscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  public updateOperationType(isDaytrade) {
    this.stock.isDayTrade = isDaytrade;
    this.updatePriceBands();
  }

  public updatePriceBands() {
    const payloadPriceBand = { symbol: this.stock.stockId, operationType: this.stock.isDayTrade ? OperationType.DayTrade : OperationType.Position };
    this.store.dispatch({ type: ResearchActions.getPriceBands, payload: payloadPriceBand });
  }

  public priceBandSubscription() {
    this.disposableSubscriptions.push(
      this.store.select((priceBandStore) => priceBandStore.research.priceBand)
        .pipe(
          filter((priceBand) => this.stock ? priceBand[this.stock.stockId] : false),
          map((priceBand) => priceBand[this.stock.stockId]),
        ).subscribe((priceBand) => this.updateGainLossValue()),
    );
  }

  // Calcula a variação dos stops
  private calculateStopPrice(purchase: boolean, gain: boolean, variation: number) {
    if ((purchase && !gain) || (!purchase && gain)) {
      variation = variation * -1;
    }
    return this.stock.typeMarket === OrderMarketTypeEnum.Future ? this.stock.unitPrice + variation : this.stock.unitPrice * (1 + (variation / 100));
  }

  private bindQuoteSubscription() {
    this.disposableSubscriptions.push(
      this.store.select((state) => state.stocksExchange.stocksMarket.quote).pipe(
        filter((item) => item[this.stock.stockId]),
        map((item) => item[this.stock.stockId])
      )
        .subscribe((quote) => {
          this.updateGainLossValue();
        }),
    );
  }

  public aboutFeesCall() {
    const reference = this.dialogsServices.showComponentAsAsidePanel(CheckoutAboutFeesComponent, true);
    this.dialogAboutFeesReference = reference as MatDialogRef<CheckoutAboutFeesComponent>;
  }
}
